import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  FaComments,
  FaChartLine,
  FaRobot,
  FaCog,
  FaSignOutAlt,
  FaBell,
  FaInstagram,
  FaTwitter,
  FaTiktok,
  FaYoutube,
  FaPlus,
  FaTachometerAlt,
  FaUserCircle,
  FaCommentDots,
  FaWallet,
  FaChartBar,
  FaMagic,
  FaMicrophone,
  FaRegClock,
  FaRegSmile,
  FaDollarSign,
  FaRegCommentDots,
  FaCheck,
  FaPlay,
  FaDownload
} from "react-icons/fa";
import { useEffect, useState } from "react";
import SEO from "../components/SEO";
import TrendAnalyzerMockUpdated from "../components/TrendAnalyzerMockUpdated.jsx";
import { supabase } from "../supabaseClient.js";
import Logo from "../assets/logo.jsx";
import UserPreferences from "../components/UserPreference.jsx";

const Dashboard = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [activePage, setActivePage] = useState("dashboard");
  const [youtubeData, setYouTubeData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchYouTubeData = async () => {
      try {
        setIsLoading(true);
        // Get the session which contains the provider_token (Google OAuth token)
        const {
          data: { session }
        } = await supabase.auth.getSession();
        if (!session?.provider_token) {
          console.log("No provider token found");
          return;
        }

        const response = await fetch(
          "https://www.googleapis.com/youtube/v3/channels?part=snippet,statistics,contentDetails&mine=true",
          {
            headers: {
              Authorization: `Bearer ${session.provider_token}`,
              "Content-Type": "application/json"
            }
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch YouTube data");
        }

        const data = await response.json();
        console.log("YouTube data:", data);

        if (data.items?.length > 0) {
          setYouTubeData(data.items[0]);
          // Save to localStorage for persistence
          localStorage.setItem("youtube_data", JSON.stringify(data.items[0]));
        }
      } catch (error) {
        console.error("Error fetching YouTube data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    // Only fetch if user is authenticated with Google
    if (user?.app_metadata?.provider === "google") {
      fetchYouTubeData();
    } else {
      // Try to load cached data
      const cachedData = localStorage.getItem("youtube_data");
      if (cachedData) {
        setYouTubeData(JSON.parse(cachedData));
      }
    }
  }, [user]);

  const handleYouTubeLogin = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          scopes: "https://www.googleapis.com/auth/youtube.readonly",
          queryParams: {
            access_type: "offline",
            prompt: "consent"
          }
        }
      });

      if (error) throw error;
    } catch (error) {
      console.error("Error connecting to YouTube:", error);
    }
  };

  const handleSignOut = () => {
    signOut();
    navigate("/");
  };

  const stats = {
    totalReplies: "1,234",
    revenueGenerated: "$3,250",
    responseRate: "98%",
    avgResponseTime: "30s"
  };

  const formatNumber = (num) => {
    if (!num) return "0";
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + "M";
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + "K";
    }
    return num;
  };

  const socialAccounts = [
    {
      platform: "Instagram",
      username: "@lifestyle_sarah",
      followers: "125K",
      status: "disconnected",
      icon: FaInstagram,
      color: "from-pink-500 to-purple-500"
    },
    {
      platform: "Twitter",
      username: "@sarah_tweets",
      followers: "45K",
      status: "disconnected",
      icon: FaTwitter,
      color: "from-blue-400 to-blue-500"
    },
    {
      platform: "TikTok",
      username: null,
      followers: null,
      status: "disconnected",
      icon: FaTiktok,
      color: "from-gray-600 to-gray-700"
    },
    {
      platform: "YouTube",
      username: youtubeData?.snippet?.title || "Not Connected",
      followers:
        formatNumber(youtubeData?.statistics?.subscriberCount) || "N/A",
      views: formatNumber(youtubeData?.statistics?.viewCount) || "N/A",
      videos: formatNumber(youtubeData?.statistics?.videoCount) || "N/A",
      thumbnail: youtubeData?.snippet?.thumbnails?.high?.url,
      customUrl: youtubeData?.snippet?.customUrl,
      status: youtubeData ? "connected" : "disconnected",
      icon: FaYoutube,
      color: "from-red-500 to-red-600"
    }
  ];

  const navigationItems = [
    {
      name: "Dashboard",
      icon: FaTachometerAlt,
      id: "dashboard"
    },
    {
      name: "Trend Analyzer",
      icon: FaChartLine,
      id: "trend analyzer",
    },
    {
      name: "Voice Clone",
      icon: FaMicrophone,
      id: "voice"
    },
    {
      name: "Pricing",
      icon: FaDollarSign,
      id: "pricing"
    },
    {
      name: "Settings",
      icon: FaCog,
      id: "settings"
    }
  ];

  const renderContent = () => {
    switch (activePage) {
      case "pricing":
        return (
          <div className="space-y-8">
            <h2 className="text-2xl font-bold text-white">Pricing Plans</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Free Plan */}
              <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      Free
                    </h3>
                    <p className="text-gray-400 mb-4">Perfect to get started</p>
                  </div>
                  <span className="bg-gray-700/50 text-gray-300 px-3 py-1 rounded-full text-sm">
                    Current
                  </span>
                </div>
                <div className="mb-6">
                  <p className="text-3xl font-bold text-white">$0</p>
                  <p className="text-gray-400">per month</p>
                </div>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center text-gray-300">
                    <FaCheck className="w-4 h-4 mr-2 text-green-400" />
                    Basic AI responses
                  </li>
                  <li className="flex items-center text-gray-300">
                    <FaCheck className="w-4 h-4 mr-2 text-green-400" />
                    100 messages/month
                  </li>
                  <li className="flex items-center text-gray-300">
                    <FaCheck className="w-4 h-4 mr-2 text-green-400" />1 social
                    account
                  </li>
                </ul>
                <button className="w-full px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors">
                  Current Plan
                </button>
              </div>

              {/* Pro Plan */}
              <div className="bg-gradient-to-br from-blue-600/20 to-gray-800/30 rounded-xl p-6 border border-blue-500/50">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      Pro
                    </h3>
                    <p className="text-gray-400 mb-4">For growing creators</p>
                  </div>
                  <span className="bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full text-sm">
                    Popular
                  </span>
                </div>
                <div className="mb-6">
                  <p className="text-3xl font-bold text-white">$29</p>
                  <p className="text-gray-400">per month</p>
                </div>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center text-gray-300">
                    <FaCheck className="w-4 h-4 mr-2 text-green-400" />
                    Advanced AI responses
                  </li>
                  <li className="flex items-center text-gray-300">
                    <FaCheck className="w-4 h-4 mr-2 text-green-400" />
                    Unlimited messages
                  </li>
                  <li className="flex items-center text-gray-300">
                    <FaCheck className="w-4 h-4 mr-2 text-green-400" />5 social
                    accounts
                  </li>
                  <li className="flex items-center text-gray-300">
                    <FaCheck className="w-4 h-4 mr-2 text-green-400" />
                    Voice cloning
                  </li>
                </ul>
                <button className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">
                  Upgrade to Pro
                </button>
              </div>

              {/* Enterprise Plan */}
              <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      Enterprise
                    </h3>
                    <p className="text-gray-400 mb-4">For large teams</p>
                  </div>
                </div>
                <div className="mb-6">
                  <p className="text-3xl font-bold text-white">Custom</p>
                  <p className="text-gray-400">contact sales</p>
                </div>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center text-gray-300">
                    <FaCheck className="w-4 h-4 mr-2 text-green-400" />
                    Custom AI training
                  </li>
                  <li className="flex items-center text-gray-300">
                    <FaCheck className="w-4 h-4 mr-2 text-green-400" />
                    Unlimited everything
                  </li>
                  <li className="flex items-center text-gray-300">
                    <FaCheck className="w-4 h-4 mr-2 text-green-400" />
                    Priority support
                  </li>
                  <li className="flex items-center text-gray-300">
                    <FaCheck className="w-4 h-4 mr-2 text-green-400" />
                    Custom integrations
                  </li>
                </ul>
                <button className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors">
                  Contact Sales
                </button>
              </div>
            </div>
          </div>
        );
      case "trend analyzer":
        return <TrendAnalyzerMockUpdated />;
      case "settings":
        return (
          <div className="space-y-8">
            <h2 className="text-2xl font-bold text-white">Settings</h2>

            {/* Profile Settings */}
            <div className="bg-gray-800/30 rounded-xl p-6 border border-gray-800/50">
              <h3 className="text-lg font-semibold text-white mb-6">
                Profile Settings
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Display Name
                  </label>
                  <input
                    type="text"
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
                    value={user?.user_metadata.full_name || ""}
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
                    value={user?.email || ""}
                    readOnly
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case "Trends Analyzer":
        return <>{/* Revenue */}</>;

      case "voice":
        return <>{/* Voice */}</>;

      default:
        return (
          <>
            <UserPreferences />
          </>
        );
    }
  };

  return (
    <>
      <SEO
        title={`Viral Voice ${
          activePage.charAt(0).toUpperCase() + activePage.slice(1)
        } - ${user?.user_metadata.full_name}`}
        description="Manage your social media presence, automate responses, and track engagement with ViralVoice intelligent dashboard."
      />
      <div className="flex h-screen bg-gray-900">
        {/* Sidebar */}
        <div className="w-64 bg-black border-r border-gray-800">
          <div className="flex items-center px-5 justify-center  h-20 border-b border-gray-800">
            <div className="text-2xl font-bold bg-gradient-to-r from-green-400 to-blue-500 bg-clip-text text-transparent">
              {/* ViralVoice AI */}
              <Logo />
            </div>
          </div>
          <nav className="mt-8">
            <div className="px-4 mb-4">
              <div className="flex items-center gap-3 px-4 py-3 bg-gray-800/50 rounded-xl">
                <img
                  className="h-10 w-10 rounded-full"
                  src={user?.user_metadata.avatar_url}
                  alt={user?.user_metadata.full_name}
                />
                <div>
                  <div className="text-sm font-medium text-white">
                    {user?.user_metadata.full_name}
                  </div>
                  <div className="text-xs text-gray-400">Free Plan</div>
                </div>
              </div>
            </div>
            <div className="px-4">
              {navigationItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActivePage(item.id)}
                  className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl mb-2 transition-colors ${
                    activePage === item.id
                      ? "bg-blue-500/20 text-blue-400"
                      : "text-gray-400 hover:bg-gray-800/50 hover:text-white"
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.name}
                </button>
              ))}
            </div>
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Top Navigation */}
          <header className="h-16 bg-gray-800/50 border-b border-gray-800">
            <div className="flex items-center justify-between h-full px-6">
              <h1 className="text-xl font-semibold text-white capitalize">
                {activePage}
              </h1>
              <div className="flex items-center gap-4">
                <button className="text-gray-400 hover:text-white">
                  <FaBell className="w-5 h-5" />
                </button>
                <button
                  onClick={handleSignOut}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
                >
                  <FaSignOutAlt className="w-4 h-4" />
                  Sign Out
                </button>
              </div>
            </div>
          </header>

          {/* Main Content Area */}
          <main className="flex-1 overflow-y-auto bg-gradient-to-b from-gray-900 to-black p-6">
            {renderContent()}
          </main>
        </div>
      </div>
    </>
  );
};

export default Dashboard;

